import { Component } from '@theme/component';
import { track } from '@theme/r-analytics';

/* ↗ .cursor/references/code-notes/r-newsletter-signup.js.md#§1 */

const KLAVIYO_HOST = 'https://a.klaviyo.com';
// ↗ .cursor/references/code-notes/r-newsletter-signup.js.md#§2
const KLAVIYO_REVISION = '2026-07-15';
const ONSITE_SCRIPT_PATTERN = /static\.klaviyo\.com\/onsite\/js\/(?:.*\/)?([A-Za-z0-9]+)\/klaviyo\.js/;

/** Reads the public company ID from the Klaviyo app embed's onsite script tag. */
function companyIdFromAppEmbed() {
  for (const script of document.querySelectorAll('script[src*="static.klaviyo.com"]')) {
    const match = ONSITE_SCRIPT_PATTERN.exec(script.getAttribute('src') ?? '');
    if (match) return match[1];
  }

  // Newer embeds expose it on the global once klaviyo.js has booted.
  return window.klaviyo?.company_id ?? null;
}

class RNewsletterSignup extends Component {
  requiredRefs = ['stepEmail', 'emailForm', 'email'];

  #abort = new AbortController();
  #email = '';

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abort;
    this.refs.emailForm.addEventListener('submit', this.#handleEmailSubmit, { signal });
    this.refs.quizForm?.addEventListener('submit', this.#handleQuizSubmit, { signal });
    this.refs.skipButton?.addEventListener('click', this.#handleSkip, { signal });

    if (!this.#companyId || !this.#listId) {
      console.warn(
        '[r-newsletter-signup] Running in demo mode — no Klaviyo company ID and/or list ID. ' +
          'Set them in Theme settings → Rocky — Newsletter, or enable the Klaviyo app embed. ' +
          'Signups are NOT being recorded.'
      );
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
    super.disconnectedCallback();
  }

  get #companyId() {
    return this.dataset.companyId || companyIdFromAppEmbed();
  }

  get #listId() {
    return this.dataset.listId || null;
  }

  get #isDemo() {
    return !this.#companyId || !this.#listId;
  }

  // ── steps ──────────────────────────────────────────────────────────────────

  /** @param {'stepEmail' | 'stepQuiz' | 'stepDone'} name */
  #showStep(name) {
    for (const step of ['stepEmail', 'stepQuiz', 'stepDone']) {
      const el = this.refs[step];
      if (el) el.hidden = step !== name;
    }

    // Move focus to the new step so the drawer's focus trap does not strand the
    // shopper on a button that has just been hidden.
    const heading = this.refs[name]?.querySelector('[data-step-heading]');
    heading?.focus();
    this.refs[name]?.scrollIntoView({ block: 'nearest' });
  }

  #setBusy(busy) {
    const button = this.refs.emailSubmit;
    if (!button) return;
    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
  }

  #showError(show) {
    if (this.refs.emailError) this.refs.emailError.hidden = !show;
  }

  // ── handlers ───────────────────────────────────────────────────────────────

  #handleEmailSubmit = async (event) => {
    event.preventDefault();

    const email = this.refs.email.value.trim();
    if (!email) return;

    this.#email = email;
    this.#showError(false);
    this.#setBusy(true);

    try {
      await this.#subscribe(email);
      this.#trackSignup('success');
      // Inline hosts (blocks/r-story-signup.liquid) have no quiz step.
      this.#showStep(this.refs.stepQuiz ? 'stepQuiz' : 'stepDone');
    } catch (error) {
      console.error('[r-newsletter-signup]', error);
      this.#trackSignup('error');
      this.#showError(true);
    } finally {
      this.#setBusy(false);
    }
  };

  /**
   * Publishes the signup outcome — the surface, never the address.
   * @param {'success' | 'error'} status
   */
  #trackSignup(status) {
    track('newsletter_signup', { source: this.#trackSource, status });
  }

  /** Declared on the host, else inferred from where the form is mounted. */
  get #trackSource() {
    if (this.dataset.rTrackSource) return this.dataset.rTrackSource;
    if (this.closest('#r-utility-drawer-subscribe')) return 'utility_drawer';
    if (this.closest('footer, .shopify-section-group-footer-group')) return 'footer';
    return 'popup';
  }

  #handleQuizSubmit = async (event) => {
    event.preventDefault();

    // The questionnaire is enrichment, not a gate: a failed property write must
    // never make an already-successful subscription look like it failed.
    try {
      await this.#saveAnswers(this.#collectAnswers());
    } catch (error) {
      console.error('[r-newsletter-signup]', error);
    }

    this.#showStep('stepDone');
  };

  #handleSkip = () => {
    this.#showStep('stepDone');
  };

  /** @returns {Record<string, string | string[]>} */
  #collectAnswers() {
    const answers = {};

    for (const group of this.querySelectorAll('[data-question]')) {
      const key = group.dataset.question;
      const checked = [...group.querySelectorAll('input:checked')].map((input) => input.value);
      if (!checked.length) continue;

      answers[key] = group.dataset.multiple === 'true' ? checked : checked[0];
    }

    return answers;
  }

  // ── Klaviyo ────────────────────────────────────────────────────────────────

  /** @param {string} url @param {object} body */
  async #post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
      signal: this.#abort.signal,
    });

    // Klaviyo answers these endpoints with 202 Accepted and an empty body.
    if (response.ok) return;

    // ↗ .cursor/references/code-notes/r-newsletter-signup.js.md#§3
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.errors?.map((error) => error.detail).filter(Boolean).join('; ') ?? '';
    } catch {
      // Non-JSON error body — the status is all we have.
    }

    throw new Error(`Klaviyo HTTP ${response.status}${detail ? ` — ${detail}` : ''}`);
  }

  /** @param {string} email */
  async #subscribe(email) {
    if (this.#isDemo) return;

    const url = `${KLAVIYO_HOST}/client/subscriptions/?company_id=${encodeURIComponent(this.#companyId)}`;

    await this.#post(url, {
      data: {
        type: 'subscription',
        attributes: {
          // Surfaces in Klaviyo as the signup source, so drawer performance can
          // be compared against the popup and the footer form.
          custom_source: this.dataset.source || 'Header subscribe drawer',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                // ↗ .cursor/references/code-notes/r-newsletter-signup.js.md#§4
                subscriptions: {
                  email: { marketing: { consent: 'SUBSCRIBED' } },
                },
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: this.#listId } },
        },
      },
    });
  }

  /** @param {Record<string, string | string[]>} properties */
  async #saveAnswers(properties) {
    if (!Object.keys(properties).length) return;
    if (this.#isDemo) {
      console.warn('[r-newsletter-signup] Demo mode — questionnaire answers not sent:', properties);
      return;
    }

    const url = `${KLAVIYO_HOST}/client/profiles/?company_id=${encodeURIComponent(this.#companyId)}`;

    await this.#post(url, {
      data: {
        type: 'profile',
        attributes: { email: this.#email, properties },
      },
    });
  }
}

if (!customElements.get('r-newsletter-signup')) {
  customElements.define('r-newsletter-signup', RNewsletterSignup);
}
